import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, Save, Loader2, User, MapPin, Plus, Trash2, Users, Edit2
} from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';

export default function ClientFormPage() {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stateOptions, setStateOptions] = useState([]);
  
  const [clientForm, setClientForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    address: {
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: 'CA',
      zip: ''
    },
    children: []
  });

  const [newChild, setNewChild] = useState({
    full_name: '',
    date_of_birth: '',
    gender: '',
    address: {
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: 'CA',
      zip: ''
    }
  });
  const [showAddChild, setShowAddChild] = useState(false);
  const [editingChildIndex, setEditingChildIndex] = useState(null);

  useEffect(() => {
    const initData = async () => {
      await fetchUser();
      await fetchStateOptions();
      if (clientId) {
        await fetchClient();
      }
      setLoading(false);
    };
    initData();
  }, [clientId]);

  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        
        if (userData.app_mode !== 'crm') {
          toast.error('CRM features require CRM subscription');
          navigate('/dashboard');
        }
      } else {
        navigate('/auth');
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchStateOptions = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/global-fields/option-lists`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Find UsaStates from system lists
        const usaStatesList = data.system_lists?.find(list => list.name === 'UsaStates');
        setStateOptions(usaStatesList?.options || ['CA']); // Fallback to CA if not found
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      setStateOptions(['CA']); // Fallback
    }
  };

  const fetchClient = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/crm/clients/${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClientForm({
          full_name: data.full_name || '',
          email: data.email || '',
          phone: data.phone || '',
          date_of_birth: data.date_of_birth || '',
          address: data.address || {
            address_line_1: '',
            address_line_2: '',
            city: '',
            state: 'CA',
            zip: ''
          },
          children: data.children || []
        });
      }
    } catch (error) {
      toast.error('Failed to load client');
      navigate('/clients');
    }
  };

  const handleAddChild = () => {
    if (!newChild.full_name.trim()) {
      toast.error('Child name is required');
      return;
    }

    if (editingChildIndex !== null) {
      // Update existing child
      const updated = [...clientForm.children];
      updated[editingChildIndex] = { ...newChild };
      setClientForm({ ...clientForm, children: updated });
      toast.success('Child updated');
    } else {
      // Add new child
      setClientForm({
        ...clientForm,
        children: [...clientForm.children, { ...newChild, child_id: `temp_${Date.now()}` }]
      });
      toast.success('Child added');
    }
    
    setNewChild({ 
      full_name: '', 
      date_of_birth: '', 
      gender: '',
      address: {
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: 'CA',
        zip: ''
      }
    });
    setShowAddChild(false);
    setEditingChildIndex(null);
  };

  const handleEditChild = (index) => {
    const child = clientForm.children[index];
    setNewChild({
      full_name: child.full_name || '',
      date_of_birth: child.date_of_birth || '',
      gender: child.gender || '',
      address: child.address || {
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: 'CA',
        zip: ''
      }
    });
    setEditingChildIndex(index);
    setShowAddChild(true);
  };

  const handleRemoveChild = (index) => {
    const updated = clientForm.children.filter((_, i) => i !== index);
    setClientForm({ ...clientForm, children: updated });
  };

  const handleSaveClient = async () => {
    if (!clientForm.full_name.trim()) {
      toast.error('Client name is required');
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('token');

    try {
      const url = clientId 
        ? `${API}/crm/clients/${clientId}`
        : `${API}/crm/clients`;
      
      const response = await fetch(url, {
        method: clientId ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(clientForm)
      });

      if (response.ok) {
        toast.success(clientId ? 'Client updated' : 'Client created');
        navigate('/clients');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save client');
      }
    } catch (error) {
      toast.error('Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <Sidebar user={user} />
      
      <div className="flex-1 flex flex-col lg:ml-64">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-6 py-8 lg:pt-8 pt-20">
            
            {/* Header */}
            <div className="mb-8">
              <Button
                variant="ghost"
                onClick={() => navigate('/clients')}
                className="rounded-xl mb-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Clients
              </Button>
              <h1 className="text-3xl font-bold text-slate-900">
                {clientId ? 'Edit Client' : 'Add New Client'}
              </h1>
              <p className="text-slate-500 mt-1">Fill in the client details below</p>
            </div>

            {/* Form */}
            <div className="space-y-6">
              {/* Client Information */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-violet-600" />
                  Client Information
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-slate-600">Full Name *</Label>
                    <Input
                      value={clientForm.full_name}
                      onChange={(e) => setClientForm({ ...clientForm, full_name: e.target.value })}
                      placeholder="John Doe"
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-slate-600">Email</Label>
                      <Input
                        type="email"
                        value={clientForm.email}
                        onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                        placeholder="john@example.com"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Phone</Label>
                      <Input
                        value={clientForm.phone}
                        onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Date of Birth</Label>
                    <Input
                      type="date"
                      value={clientForm.date_of_birth}
                      onChange={(e) => setClientForm({ ...clientForm, date_of_birth: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-violet-600" />
                  Address
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-slate-600">Address Line 1</Label>
                    <Input
                      value={clientForm.address.address_line_1}
                      onChange={(e) => setClientForm({
                        ...clientForm,
                        address: { ...clientForm.address, address_line_1: e.target.value }
                      })}
                      placeholder="123 Main Street"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Address Line 2</Label>
                    <Input
                      value={clientForm.address.address_line_2}
                      onChange={(e) => setClientForm({
                        ...clientForm,
                        address: { ...clientForm.address, address_line_2: e.target.value }
                      })}
                      placeholder="Apt 4B"
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm text-slate-600">City</Label>
                      <Input
                        value={clientForm.address.city}
                        onChange={(e) => setClientForm({
                          ...clientForm,
                          address: { ...clientForm.address, city: e.target.value }
                        })}
                        placeholder="Los Angeles"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">State</Label>
                      <select
                        value={clientForm.address.state}
                        onChange={(e) => setClientForm({
                          ...clientForm,
                          address: { ...clientForm.address, state: e.target.value }
                        })}
                        className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white"
                      >
                        {stateOptions.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">ZIP</Label>
                      <Input
                        value={clientForm.address.zip}
                        onChange={(e) => setClientForm({
                          ...clientForm,
                          address: { ...clientForm.address, zip: e.target.value }
                        })}
                        placeholder="90001"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Children */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <Users className="w-5 h-5 text-violet-600" />
                    Children
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddChild(true)}
                    className="rounded-lg"
                    data-testid="open-add-child-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Child
                  </Button>
                </div>

                {clientForm.children.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No children added</p>
                ) : (
                  <div className="space-y-2">
                    {clientForm.children.map((child, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-900">{child.full_name}</p>
                          <p className="text-xs text-slate-500">
                            {child.date_of_birth && `DOB: ${child.date_of_birth}`}
                            {child.gender && ` • ${child.gender}`}
                            {child.address?.city && ` • ${child.address.city}, ${child.address.state || 'CA'}`}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditChild(index)}
                            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveChild(index)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add/Edit Child Form */}
                {showAddChild && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                    <h3 className="font-semibold text-slate-900 mb-3">
                      {editingChildIndex !== null ? 'Edit Child' : 'Add Child'}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm text-slate-600">Child's Full Name *</Label>
                        <Input
                          value={newChild.full_name}
                          onChange={(e) => setNewChild({ ...newChild, full_name: e.target.value })}
                          placeholder="Jane Doe"
                          className="mt-1"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm text-slate-600">Date of Birth</Label>
                          <Input
                            type="date"
                            value={newChild.date_of_birth}
                            onChange={(e) => setNewChild({ ...newChild, date_of_birth: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-slate-600">Gender</Label>
                          <select
                            value={newChild.gender}
                            onChange={(e) => setNewChild({ ...newChild, gender: e.target.value })}
                            className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white"
                          >
                            <option value="">Select</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                          </select>
                        </div>
                      </div>
                      
                      {/* Child Address */}
                      <div className="pt-2 border-t border-blue-200">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-sm text-slate-700 font-semibold">Child's Address</Label>
                          <label className="flex items-center gap-2 cursor-pointer text-xs text-blue-600 hover:text-blue-700">
                            <input
                              type="checkbox"
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNewChild(prev => ({
                                    ...prev,
                                    address: {
                                      address_line_1: clientForm.address.address_line_1 || '',
                                      address_line_2: clientForm.address.address_line_2 || '',
                                      city: clientForm.address.city || '',
                                      state: clientForm.address.state || 'CA',
                                      zip: clientForm.address.zip || ''
                                    }
                                  }));
                                  toast.success('Address copied from client');
                                }
                              }}
                              className="w-4 h-4 text-blue-600 rounded"
                            />
                            <span>Same as client</span>
                          </label>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <Label className="text-xs text-slate-600">Address Line 1</Label>
                            <Input
                              value={newChild.address.address_line_1}
                              onChange={(e) => setNewChild({
                                ...newChild,
                                address: { ...newChild.address, address_line_1: e.target.value }
                              })}
                              placeholder="123 Main Street"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-600">Address Line 2</Label>
                            <Input
                              value={newChild.address.address_line_2}
                              onChange={(e) => setNewChild({
                                ...newChild,
                                address: { ...newChild.address, address_line_2: e.target.value }
                              })}
                              placeholder="Apt 4B"
                              className="mt-1"
                            />
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <Label className="text-xs text-slate-600">City</Label>
                              <Input
                                value={newChild.address.city}
                                onChange={(e) => setNewChild({
                                  ...newChild,
                                  address: { ...newChild.address, city: e.target.value }
                                })}
                                placeholder="Los Angeles"
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-600">State</Label>
                              <select
                                value={newChild.address.state}
                                onChange={(e) => setNewChild({
                                  ...newChild,
                                  address: { ...newChild.address, state: e.target.value }
                                })}
                                className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-xs bg-white"
                              >
                                {stateOptions.map(state => (
                                  <option key={state} value={state}>{state}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <Label className="text-xs text-slate-600">ZIP</Label>
                              <Input
                                value={newChild.address.zip}
                                onChange={(e) => setNewChild({
                                  ...newChild,
                                  address: { ...newChild.address, zip: e.target.value }
                                })}
                                placeholder="90001"
                                className="mt-1"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 pt-2">
                        <Button
                          onClick={handleAddChild}
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid="add-child-submit-btn"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          {editingChildIndex !== null ? 'Update' : 'Add'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowAddChild(false);
                            setEditingChildIndex(null);
                            setNewChild({ 
                              full_name: '', 
                              date_of_birth: '', 
                              gender: '',
                              address: {
                                address_line_1: '',
                                address_line_2: '',
                                city: '',
                                state: 'CA',
                                zip: ''
                              }
                            });
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between py-6">
                <Button
                  variant="outline"
                  onClick={() => navigate('/clients')}
                  className="rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveClient}
                  disabled={saving}
                  className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white px-8"
                  data-testid="save-client-btn"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> {clientId ? 'Update Client' : 'Create Client'}</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
